import type { AppLanguage } from '../i18n/types';
import {
  OPENING_TIMES_WEEKDAY_ORDER,
  findOpeningTimesWeekdayKey,
  isOpeningTimesWeekdayKey,
} from './openingTimesWeekdays';

const DEFAULT_CREATE_DAY_HOURS = { open: '09:00', close: '19:00' } as const;

const HE_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'] as const;

/** US-style Mon–Fri weekdays. */
const EN_WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

export type OpeningTimesScheduleGroupKey = 'weekdays' | 'friday' | 'saturday' | 'sunday';

export type OpeningTimesScheduleGroup = {
  key: OpeningTimesScheduleGroupKey;
  days: readonly string[];
};

const HE_SCHEDULE_GROUPS: OpeningTimesScheduleGroup[] = [
  { key: 'weekdays', days: HE_WEEKDAYS },
  { key: 'friday', days: ['friday'] },
  { key: 'saturday', days: ['saturday'] },
];

const EN_SCHEDULE_GROUPS: OpeningTimesScheduleGroup[] = [
  { key: 'weekdays', days: EN_WEEKDAYS },
  { key: 'saturday', days: ['saturday'] },
  { key: 'sunday', days: ['sunday'] },
];

export function openingTimesScheduleGroupsForLocale(
  language: AppLanguage,
): readonly OpeningTimesScheduleGroup[] {
  return language === 'he' ? HE_SCHEDULE_GROUPS : EN_SCHEDULE_GROUPS;
}

export function openingTimesScheduleGroupLabelKey(key: OpeningTimesScheduleGroupKey): string {
  switch (key) {
    case 'weekdays':
      return 'openingTimes.weekdaysSection';
    case 'friday':
      return 'openingTimes.fridaySection';
    case 'saturday':
      return 'openingTimes.saturdaySection';
    case 'sunday':
      return 'openingTimes.sundaySection';
    default:
      return 'openingTimes.weekdaysSection';
  }
}

export function openingTimesWeekdaysForLocale(language: AppLanguage): readonly string[] {
  const weekdays = openingTimesScheduleGroupsForLocale(language).find((g) => g.key === 'weekdays');
  return weekdays?.days ?? (language === 'he' ? HE_WEEKDAYS : EN_WEEKDAYS);
}

/** Fri–Sat (IL) or Sat–Sun (US) — not shown as one UI group anymore. */
export function openingTimesWeekendForLocale(language: AppLanguage): readonly string[] {
  if (language === 'he') {
    return ['friday', 'saturday'];
  }
  return ['saturday', 'sunday'];
}

export function openingTimesDefaultDaysForLocale(language: AppLanguage): readonly string[] {
  return openingTimesScheduleGroupsForLocale(language).flatMap((group) => group.days);
}

/** Default opening hours for a new asset create form (all seven weekdays). */
export function buildDefaultAssetCreateOpeningTimes(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const day of OPENING_TIMES_WEEKDAY_ORDER) {
    out[day] = { ...DEFAULT_CREATE_DAY_HOURS };
  }
  return out;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function resolveOpeningTimesStoredWeekdayKey(
  data: Record<string, unknown>,
  canonical: string,
): string {
  return findOpeningTimesWeekdayKey(data, canonical) ?? canonical;
}

function isOpenClosePair(value: unknown): value is { open: string; close: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'open' in value &&
    'close' in value &&
    typeof (value as { open: unknown }).open === 'string' &&
    typeof (value as { close: unknown }).close === 'string'
  );
}

function isExclusiveClosedMarkerObject(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    'closed' in value &&
    (value as { closed: unknown }).closed === true &&
    Object.keys(value).length === 1
  );
}

/** Normalize API/UI day values for comparison and group writes. */
export function openingTimesNormalizeDayValue(
  value: unknown,
): 'closed' | { open: string; close: string } {
  if (value === 'closed' || value === '' || value === null || value === undefined) {
    return 'closed';
  }
  if (isExclusiveClosedMarkerObject(value)) {
    return 'closed';
  }
  if (isOpenClosePair(value)) {
    const open = String(value.open).trim();
    const close = String(value.close).trim();
    if (open === '' && close === '') {
      return 'closed';
    }
    return { open, close };
  }
  return 'closed';
}

/** True when create-form opening_times was cleared or never filled in. */
export function isAssetCreateOpeningTimesUnset(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return true;
  }
  const keys = Object.keys(value);
  if (keys.length === 0) {
    return true;
  }
  return keys.every((key) => {
    if (!isOpeningTimesWeekdayKey(key)) {
      return true;
    }
    const normalized = openingTimesNormalizeDayValue(value[key]);
    return normalized === 'closed';
  });
}

export function openingTimesDayValuesEqual(a: unknown, b: unknown): boolean {
  const na = openingTimesNormalizeDayValue(a);
  const nb = openingTimesNormalizeDayValue(b);
  if (na === 'closed' && nb === 'closed') {
    return true;
  }
  if (na === 'closed' || nb === 'closed') {
    return false;
  }
  return na.open === nb.open && na.close === nb.close;
}

export function openingTimesCloneDayValue(value: unknown): unknown {
  const normalized = openingTimesNormalizeDayValue(value);
  if (normalized === 'closed') {
    return 'closed';
  }
  return { open: normalized.open, close: normalized.close };
}

export function openingTimesGetDayValue(
  data: Record<string, unknown>,
  canonical: string,
): unknown {
  const key = resolveOpeningTimesStoredWeekdayKey(data, canonical);
  const raw = data[key];
  if (raw === undefined || raw === null || raw === '') {
    return 'closed';
  }
  return raw;
}

/** Shared schedule for a locale group: most common value among explicitly stored days. */
export function deriveOpeningTimesGroupSchedule(
  data: Record<string, unknown>,
  groupDays: readonly string[],
): unknown {
  const counts = new Map<string, { value: unknown; count: number }>();
  let firstExplicit: unknown | null = null;

  for (const day of groupDays) {
    const key = resolveOpeningTimesStoredWeekdayKey(data, day);
    if (!(key in data)) {
      continue;
    }
    const value = data[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    if (firstExplicit === null) {
      firstExplicit = value;
    }
    const sig = JSON.stringify(openingTimesNormalizeDayValue(value));
    const existing = counts.get(sig);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(sig, { value, count: 1 });
    }
  }

  if (counts.size === 0) {
    return 'closed';
  }

  let best: { value: unknown; count: number } | null = null;
  const firstSig =
    firstExplicit === null
      ? null
      : JSON.stringify(openingTimesNormalizeDayValue(firstExplicit));

  for (const entry of Array.from(counts.values())) {
    if (!best || entry.count > best.count) {
      best = entry;
      continue;
    }
    if (entry.count === best.count && firstSig !== null) {
      const entrySig = JSON.stringify(openingTimesNormalizeDayValue(entry.value));
      const bestSig = JSON.stringify(openingTimesNormalizeDayValue(best.value));
      if (entrySig === firstSig && bestSig !== firstSig) {
        best = entry;
      }
    }
  }

  return best?.value ?? 'closed';
}

export function openingTimesDaysWithDistinctSchedule(
  data: Record<string, unknown>,
  groupDays: readonly string[],
  groupSchedule: unknown,
): string[] {
  return groupDays.filter((day) => {
    const key = resolveOpeningTimesStoredWeekdayKey(data, day);
    if (!(key in data)) {
      return false;
    }
    return !openingTimesDayValuesEqual(data[key], groupSchedule);
  });
}

export function openingTimesGroupDaysForDay(
  day: string,
  language: AppLanguage,
): readonly string[] {
  const canonical = day.trim().toLowerCase();
  for (const group of openingTimesScheduleGroupsForLocale(language)) {
    if (group.days.includes(canonical)) {
      return group.days;
    }
  }
  return openingTimesWeekdaysForLocale(language);
}

/** Weekday rows with hours that differ from their group default, plus UI-pending overrides. */
export function openingTimesEffectiveOverrides(
  data: Record<string, unknown>,
  language: AppLanguage,
  pendingOverrides: ReadonlySet<string> = new Set(),
): string[] {
  const derived = new Set<string>(Array.from(pendingOverrides));
  for (const group of openingTimesScheduleGroupsForLocale(language)) {
    if (group.days.length <= 1) {
      continue;
    }
    const groupSchedule = deriveOpeningTimesGroupSchedule(data, group.days);
    for (const day of openingTimesDaysWithDistinctSchedule(data, group.days, groupSchedule)) {
      derived.add(day);
    }
  }
  return OPENING_TIMES_WEEKDAY_ORDER.filter((day) => derived.has(day));
}

export function openingTimesDaysAvailableToOverride(
  effectiveOverrides: readonly string[],
): string[] {
  const used = new Set(effectiveOverrides);
  return OPENING_TIMES_WEEKDAY_ORDER.filter((day) => !used.has(day));
}

/** Weekdays in the multi-day group that are not yet individually customized. */
export function openingTimesWeekdaysAvailableToCustomize(
  language: AppLanguage,
  effectiveOverrides: readonly string[],
): string[] {
  const used = new Set(effectiveOverrides);
  return openingTimesWeekdaysForLocale(language).filter((day) => !used.has(day));
}

/** Materialize every weekday in the locale group as its own editable row. */
export function expandOpeningTimesWeekdaysToIndividual(
  data: Record<string, unknown>,
  language: AppLanguage,
): { nextData: Record<string, unknown>; expandedDays: string[] } {
  const groupDays = openingTimesWeekdaysForLocale(language);
  const schedule = deriveOpeningTimesGroupSchedule(data, groupDays);
  let next = data;
  for (const day of groupDays) {
    next = applyOpeningTimesDayOverride(next, day, schedule);
  }
  return { nextData: next, expandedDays: [...groupDays] };
}

/** Apply one shared schedule to every day in the group except explicit overrides. */
export function applyOpeningTimesGroupSchedule(
  data: Record<string, unknown>,
  groupDays: readonly string[],
  skipDays: ReadonlySet<string>,
  nextValue: unknown,
): Record<string, unknown> {
  const next = { ...data };
  const cloned = openingTimesCloneDayValue(nextValue);
  for (const day of groupDays) {
    if (skipDays.has(day)) {
      continue;
    }
    const storedKey = resolveOpeningTimesStoredWeekdayKey(next, day);
    if (storedKey !== day && storedKey in next) {
      delete next[storedKey];
    }
    next[day] = openingTimesCloneDayValue(cloned);
  }
  return next;
}

export function applyOpeningTimesDayOverride(
  data: Record<string, unknown>,
  day: string,
  nextValue: unknown,
): Record<string, unknown> {
  const next = { ...data };
  const storedKey = resolveOpeningTimesStoredWeekdayKey(next, day);
  if (storedKey !== day && storedKey in next) {
    delete next[storedKey];
  }
  next[day] = openingTimesCloneDayValue(nextValue);
  return next;
}

export function removeOpeningTimesDayOverride(
  data: Record<string, unknown>,
  day: string,
  language: AppLanguage,
): Record<string, unknown> {
  const groupDays = openingTimesGroupDaysForDay(day, language);
  const groupSchedule = deriveOpeningTimesGroupSchedule(data, groupDays);
  return applyOpeningTimesDayOverride(data, day, groupSchedule);
}

export { isOpeningTimesWeekdayKey };
